import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Box, Button, Chip, CircularProgress, Container, Dialog,
  DialogActions, DialogContent, DialogTitle, Divider, Drawer,
  FormControlLabel, Grid, IconButton, LinearProgress, Paper, Stack,
  Switch, Tab, Tabs, TextField, Typography,
} from "@mui/material";
import { MdCloudUpload, MdDelete, MdEdit, MdImage, MdSave } from "react-icons/md";
import { db, storage } from "../../firebase/firebase-config";
import Colors from "../../assets/styles/Colors";
import { getHomeConfigBanner, setHomeConfigBanner } from "../store/actions/homeConfig";

// ── Tab definitions ───────────────────────────────────────────────────────────

const ITEM_TABS = [
  {
    key: "eventos",
    label: "Eventos y Clases",
    subtitle: "App Inicio › EVENTOS Y CLASES",
    query: (col) => col.where("category", "==", "events").orderBy("orderNumber", "asc"),
    defaults: { category: "events", tags: ["events"] },
  },
  {
    key: "recomendado",
    label: "Recomendado / Comunidad",
    subtitle: "App Inicio › RECOMENDADO PARA TI Y COMUNIDAD",
    query: (col) => col.where("tags", "array-contains", "recommended").orderBy("orderNumber", "asc"),
    defaults: { category: "recommended", tags: ["recommended"] },
  },
];

const ITEMS_COL = () => db.collection("businesses").doc("root").collection("items");

// ── OwnersBannerPanel ─────────────────────────────────────────────────────────

const OWNERS_SECTION = {
  key: "owners_banner",
  label: "Conoce a Keylin y Carolina",
  appSection: "Inicio › CONOCE A KEYLIN Y CAROLINA",
  recommendation: "Imagen horizontal 16:9. Banner principal de owners.",
};

function OwnersBannerPanel() {
  const [items, setItems]     = useState([]);
  const [saving, setSaving]   = useState(false);
  const [status, setStatus]   = useState("");

  const load = useCallback(async () => {
    const data = await getHomeConfigBanner(OWNERS_SECTION.key);
    const savedItems = Array.isArray(data?.items) && data.items.length
      ? data.items
      : (Array.isArray(data?.imageURLs) && data.imageURLs.length
          ? data.imageURLs.map((imageURL, index) => ({
              title: index === 0 ? data.title || "" : "",
              subtitle: index === 0 ? data.subtitle || "" : "",
              clickMe: index === 0 ? data.clickMe || "" : "",
              imageURL,
            }))
          : (data?.imageURL ? [{ title: data.title || "", subtitle: data.subtitle || "", clickMe: data.clickMe || "", imageURL: data.imageURL }] : []));
    setItems(savedItems.map((item, index) => ({
      id: `${Date.now()}_${index}`,
      title: item.title || "",
      subtitle: item.subtitle || "",
      clickMe: item.clickMe || "",
      imageURL: item.imageURL || "",
      preview: item.imageURL || "",
      file: null,
    })));
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    return () => items.forEach((item) => {
      if (item.preview?.startsWith("blob:")) URL.revokeObjectURL(item.preview);
    });
  }, [items]);

  const addItem = () => {
    setItems((current) => [...current, {
      id: `${Date.now()}_${current.length}`,
      title: "",
      subtitle: "",
      clickMe: "",
      imageURL: "",
      preview: "",
      file: null,
    }]);
  };

  const updateItem = (index, patch) => {
    setItems((current) => current.map((item, i) => i === index ? { ...item, ...patch } : item));
  };

  const pickImage = (index, file) => {
    if (!file) return;
    setItems((current) => current.map((item, i) => {
      if (i !== index) return item;
      if (item.preview?.startsWith("blob:")) URL.revokeObjectURL(item.preview);
      return { ...item, file, preview: URL.createObjectURL(file) };
    }));
  };

  const removeItem = (index) => {
    setItems((current) => {
      const item = current[index];
      if (item?.preview?.startsWith("blob:")) URL.revokeObjectURL(item.preview);
      return current.filter((_, i) => i !== index);
    });
  };

  const handleSave = async () => {
    setSaving(true);
    setStatus("");
    try {
      await setHomeConfigBanner(OWNERS_SECTION.key, { items });
      await load();
      setStatus("saved");
      setTimeout(() => setStatus(""), 3000);
    } catch {
      setStatus("error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Paper sx={{ borderRadius: 3, backgroundColor: Colors.bgSecondary, border: `1px solid ${Colors.borderSecondary}`, overflow: "hidden", maxWidth: 920 }}>
      <Box sx={{ px: 2.5, py: 1.75, backgroundColor: Colors.bgTertiary, borderBottom: `1px solid ${Colors.borderSecondary}` }}>
        <Typography variant="subtitle1" sx={{ color: Colors.textPrimary, fontWeight: 700 }}>{OWNERS_SECTION.label}</Typography>
        <Typography variant="caption" sx={{ color: Colors.textSecondary }}>{OWNERS_SECTION.appSection}</Typography>
      </Box>
      <Box sx={{ p: 2.5 }}>
        <Stack spacing={2}>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Typography variant="caption" sx={{ color: Colors.textSecondary }}>{OWNERS_SECTION.recommendation}</Typography>
            <Button variant="outlined" onClick={addItem} startIcon={<MdImage />} sx={{ borderColor: Colors.borderPrimary, color: Colors.textPrimary, textTransform: "none", borderRadius: 2 }}>
              Agregar item
            </Button>
          </Stack>

          {items.length === 0 && (
            <Box sx={{ py: 4, textAlign: "center", border: `1px dashed ${Colors.borderPrimary}`, borderRadius: 2 }}>
              <Typography sx={{ color: Colors.textMuted, mb: 1 }}>Sin items. Agrega el primero.</Typography>
              <Button variant="contained" onClick={addItem} sx={{ backgroundColor: Colors.primary, textTransform: "none" }}>Agregar item</Button>
            </Box>
          )}

          <Stack spacing={1.5}>
            {items.map((item, index) => (
              <Paper key={item.id} sx={{ p: 1.5, borderRadius: 2, backgroundColor: Colors.bgTertiary, border: `1px solid ${Colors.borderSecondary}` }}>
                <Grid container spacing={1.5}>
                  <Grid item xs={12} sm={4}>
                    <Button component="label" fullWidth sx={{ p: 0, height: 138, borderRadius: 2, overflow: "hidden", border: `1px dashed ${Colors.borderPrimary}`, backgroundColor: Colors.bgSecondary }}>
                      {item.preview
                        ? <img src={item.preview} alt="" style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
                        : <Stack alignItems="center" spacing={0.75}>
                            <MdCloudUpload size={24} color={Colors.textSecondary} />
                            <Typography variant="caption" sx={{ color: Colors.textSecondary, textTransform: "none" }}>Imagen</Typography>
                          </Stack>
                      }
                      <input hidden type="file" accept="image/*" onChange={(e) => { pickImage(index, e.target.files?.[0]); e.target.value = ""; }} />
                    </Button>
                  </Grid>
                  <Grid item xs={12} sm={8}>
                    <Stack spacing={1}>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Typography variant="caption" sx={{ color: Colors.textMuted, fontWeight: 700 }}>ITEM #{index + 1}</Typography>
                        <IconButton size="small" onClick={() => removeItem(index)} sx={{ ml: "auto", color: Colors.error }}>
                          <MdDelete />
                        </IconButton>
                      </Stack>
                      <TextField label="Título" value={item.title} onChange={(e) => updateItem(index, { title: e.target.value })} fullWidth size="small" sx={{ "& .MuiInputLabel-root": { color: Colors.textSecondary } }} />
                      <TextField label="Subtítulo / descripción" value={item.subtitle} onChange={(e) => updateItem(index, { subtitle: e.target.value })} fullWidth size="small" multiline minRows={2} sx={{ "& .MuiInputLabel-root": { color: Colors.textSecondary } }} />
                      <TextField label="URL de destino" value={item.clickMe} onChange={(e) => updateItem(index, { clickMe: e.target.value })} fullWidth size="small" placeholder="https://..." sx={{ "& .MuiInputLabel-root": { color: Colors.textSecondary } }} />
                    </Stack>
                  </Grid>
                </Grid>
              </Paper>
            ))}
          </Stack>

          <Typography variant="caption" sx={{ color: Colors.textSecondary }}>{OWNERS_SECTION.recommendation}</Typography>

          {status === "saved" && <Typography variant="caption" sx={{ color: Colors.success, fontWeight: 600 }}>Banner guardado.</Typography>}
          {status === "error" && <Typography variant="caption" sx={{ color: Colors.error }}>Error al guardar.</Typography>}

          <Button variant="contained" onClick={handleSave} disabled={saving}
            startIcon={saving ? <CircularProgress size={16} sx={{ color: Colors.white }} /> : <MdSave />}
            sx={{ alignSelf: "flex-end", backgroundColor: Colors.primary, color: Colors.white, textTransform: "none", borderRadius: 2 }}
          >
            {saving ? "Guardando..." : "Guardar"}
          </Button>
        </Stack>
      </Box>
    </Paper>
  );
}

// ── Access chip config ────────────────────────────────────────────────────────

const PLAN_ACCESS = {
  public: { label: "Todos",         bg: "#1a1a3e", color: "#818cf8" },
  access: { label: "NuCore Access", bg: "#0a2e1f", color: "#34d399" },
  pro:    { label: "NuCore Pro",    bg: "#102a3a", color: "#38bdf8" },
  elite:  { label: "NuCore Elite",  bg: "#2d1c00", color: "#fbbf24" },
};

function normalizeAccessKey(value) {
  const key = String(value || "").toLowerCase();
  if (key === "all" || key === "public") return "public";
  if (key === "members" || key === "member" || key === "access") return "access";
  if (key === "gold" || key === "goldplan" || key === "elite") return "elite";
  if (key === "pro") return "pro";
  return "";
}

function accessKeyFor(item) {
  return normalizeAccessKey(item.minimumPlanKey || item.requiredPlan || item.target)
    || (item.isGoldPlan ? "elite" : item.isMembersOnly ? "access" : "public");
}

function accessFieldsFor(key) {
  const normalized = normalizeAccessKey(key) || "public";
  return {
    minimumPlanKey: normalized,
    requiredPlan: normalized,
    isMembersOnly: normalized !== "public",
    isGoldPlan: normalized === "elite",
  };
}

function accessChip(item) {
  return PLAN_ACCESS[accessKeyFor(item)] || PLAN_ACCESS.public;
}

// ── HomeBannersTab — card-grid style matching BannersView ─────────────────────

function HomeBannersTab({ tab }) {
  const [items,    setItems]    = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [drawer,   setDrawer]   = useState(null);
  const [confirm,  setConfirm]  = useState(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    setLoading(true);
    const unsub = tab.query(ITEMS_COL()).onSnapshot(
      (snap) => { setItems(snap.docs.map((d) => ({ id: d.id, ...d.data() }))); setLoading(false); },
      () => setLoading(false)
    );
    return unsub;
  }, [tab]);

  const handleDelete = async () => {
    if (!confirm) return;
    setDeleting(true);
    try { await ITEMS_COL().doc(confirm.id).delete(); setConfirm(null); }
    catch (err) { console.error(err); }
    finally { setDeleting(false); }
  };

  const linkLabel = (item) => item.streamURL || item.videoURL || item.clickMe || "";

  return (
    <Box>
      {/* Row: subtitle + add button */}
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2.5 }}>
        <Typography variant="caption" sx={{ color: Colors.textSecondary }}>{tab.subtitle}</Typography>
        <Button
          variant="contained"
          onClick={() => setDrawer({ isNew: true })}
          sx={{ backgroundColor: Colors.primary, color: Colors.white, textTransform: "none", borderRadius: 2, "&:hover": { backgroundColor: Colors.primaryDark } }}
        >
          + Nuevo
        </Button>
      </Stack>

      {loading && (
        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
          <CircularProgress sx={{ color: Colors.primary }} />
        </Box>
      )}

      {!loading && items.length === 0 && (
        <Box sx={{ py: 8, textAlign: "center" }}>
          <Typography sx={{ color: Colors.textMuted }}>Sin ítems. Agrega el primero.</Typography>
        </Box>
      )}

      <Grid container spacing={2}>
        {items.map((item) => {
          const chip = accessChip(item);
          const link = linkLabel(item);
          return (
            <Grid item xs={12} sm={6} md={4} key={item.id}>
              <Paper sx={{ borderRadius: 3, overflow: "hidden", border: `1px solid ${Colors.borderSecondary}`, backgroundColor: Colors.bgSecondary, height: "100%", display: "flex", flexDirection: "column" }}>

                {/* Cover */}
                <Box sx={{ position: "relative", height: 130, background: `linear-gradient(135deg, ${Colors.bgTertiary}, ${Colors.bgElevated})`, overflow: "hidden", flexShrink: 0 }}>
                  {item.imageURL
                    ? <img src={item.imageURL} alt={item.name} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                    : <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%", opacity: 0.3 }}>
                        <MdImage size={36} color={Colors.textSecondary} />
                      </Box>
                  }
                  {/* Chips overlay */}
                  <Box sx={{ position: "absolute", top: 8, left: 8, display: "flex", gap: 0.75 }}>
                    <Chip label={chip.label} size="small" sx={{ backgroundColor: chip.bg, color: chip.color, fontWeight: 700, fontSize: "0.68rem" }} />
                    <Chip
                      label={item.isActive !== false ? "Activo" : "Inactivo"}
                      size="small"
                      sx={{ backgroundColor: item.isActive !== false ? "#0a2e1f" : "#2a2a2a", color: item.isActive !== false ? "#34d399" : Colors.textMuted, fontWeight: 700, fontSize: "0.68rem" }}
                    />
                  </Box>
                  {/* Order badge */}
                  <Box sx={{ position: "absolute", top: 8, right: 8 }}>
                    <Chip label={`#${item.orderNumber ?? 0}`} size="small" sx={{ backgroundColor: "rgba(0,0,0,0.55)", color: Colors.textSecondary, fontSize: "0.65rem", fontWeight: 600 }} />
                  </Box>
                </Box>

                {/* Body */}
                <Box sx={{ p: 2, display: "flex", flexDirection: "column", flex: 1 }}>
                  <Typography variant="subtitle1" sx={{ color: Colors.textPrimary, fontWeight: 700, mb: 0.5, lineHeight: 1.25 }}>
                    {item.name || "(Sin nombre)"}
                  </Typography>
                  {item.description && (
                    <Typography variant="caption" sx={{ color: Colors.textSecondary, display: "block", mb: 0.75 }}>
                      {item.description}
                    </Typography>
                  )}
                  {link && (
                    <Typography variant="caption" sx={{ color: Colors.textMuted, fontFamily: "monospace", display: "block", mb: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {link}
                    </Typography>
                  )}

                  <Stack direction="row" spacing={1} sx={{ mt: "auto" }}>
                    <Button
                      fullWidth variant="outlined" startIcon={<MdEdit />}
                      onClick={() => setDrawer(item)}
                      sx={{ textTransform: "none", borderRadius: 2, borderColor: Colors.borderSecondary, color: Colors.textSecondary, "&:hover": { borderColor: Colors.primary, color: Colors.textPrimary } }}
                    >
                      Editar
                    </Button>
                    <IconButton
                      onClick={() => setConfirm(item)}
                      size="small"
                      sx={{ color: Colors.error, border: `1px solid ${Colors.borderSecondary}`, borderRadius: 2, px: 1.25 }}
                      title="Eliminar"
                    >
                      <MdDelete size={16} />
                    </IconButton>
                  </Stack>
                </Box>
              </Paper>
            </Grid>
          );
        })}
      </Grid>

      {drawer !== null && <ItemDrawer item={drawer} defaults={tab.defaults} onClose={() => setDrawer(null)} />}

      <Dialog open={!!confirm} onClose={() => !deleting && setConfirm(null)}
        PaperProps={{ sx: { backgroundColor: Colors.bgSecondary, border: `1px solid ${Colors.borderSecondary}` } }}>
        <DialogTitle sx={{ color: Colors.textPrimary }}>Confirmar eliminación</DialogTitle>
        <DialogContent>
          <Typography sx={{ color: Colors.textSecondary }}>
            ¿Eliminar "{confirm?.name}"? Esta acción no se puede deshacer.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirm(null)} disabled={deleting} sx={{ color: Colors.textSecondary }}>Cancelar</Button>
          <Button onClick={handleDelete} disabled={deleting} sx={{ color: Colors.error }}>
            {deleting ? "Eliminando..." : "Eliminar"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

// ── ItemDrawer ────────────────────────────────────────────────────────────────

function ItemDrawer({ item, defaults, onClose }) {
  const isNew  = !!item.isNew;
  const fileRef = useRef(null);

  const [name,        setName]        = useState(item.name        || "");
  const [description, setDescription] = useState(item.description || "");
  const [imageURL,    setImageURL]    = useState(item.imageURL    || "");
  const [videoURL,    setVideoURL]    = useState(item.videoURL    || "");
  const [streamURL,   setStreamURL]   = useState(item.streamURL   || "");
  const [clickMe,     setClickMe]     = useState(item.clickMe     || "");
  const [orderNumber, setOrderNumber] = useState(item.orderNumber ?? 0);
  const [isActive,    setIsActive]    = useState(item.isActive    !== false);
  const [accessKey,   setAccessKey]   = useState(accessKeyFor(item));

  const [imageFile,    setImageFile]    = useState(null);
  const [imagePreview, setImagePreview] = useState(item.imageURL || "");
  const [uploadPct,    setUploadPct]    = useState(0);
  const [saving,       setSaving]       = useState(false);
  const [saveError,    setSaveError]    = useState("");

  const handleImagePick = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
  };

  const uploadImage = async (docId) => {
    if (!imageFile) return imageURL;
    const ref = storage.ref(`home_items/${docId}/${imageFile.name}`);
    const task = ref.put(imageFile);
    await new Promise((res, rej) => {
      task.on("state_changed",
        (snap) => setUploadPct(Math.round(snap.bytesTransferred / snap.totalBytes * 100)),
        rej, res
      );
    });
    return await ref.getDownloadURL();
  };

  const handleSave = async () => {
    if (!name.trim()) { setSaveError("El nombre es requerido."); return; }
    setSaving(true);
    setSaveError("");
    try {
      const docId = item.id || `item_${Date.now()}`;
      const uploadedURL = await uploadImage(docId);
      await ITEMS_COL().doc(docId).set({
        ...defaults,
        name:         name.trim(),
        description:  description.trim(),
        imageURL:     uploadedURL || imageURL || "",
        videoURL:     videoURL.trim(),
        streamURL:    streamURL.trim(),
        clickMe:      clickMe.trim(),
        orderNumber:  Number(orderNumber) || 0,
        isActive,
        ...accessFieldsFor(accessKey),
        cdate:        item.cdate || new Date(),
      }, { merge: true });
      onClose();
    } catch (err) {
      setSaveError(err.message || "Error al guardar.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Drawer anchor="right" open onClose={saving ? undefined : onClose}
      PaperProps={{ sx: { top: "72px", height: "calc(100% - 72px)" } }}>
      <Box sx={{ width: { xs: "100vw", sm: 460 }, p: 3, backgroundColor: Colors.bgSecondary, height: "100%", overflowY: "auto" }}>
        <Stack spacing={2.5}>
          <Typography variant="h6" sx={{ color: Colors.textPrimary }}>
            {isNew ? "Nuevo ítem" : `Editar · ${item.name || "ítem"}`}
          </Typography>

          {saveError && <Typography variant="body2" color="error">{saveError}</Typography>}

          {/* Image */}
          <Box
            sx={{
              position: "relative", height: 160, borderRadius: 2, overflow: "hidden",
              background: imagePreview ? "none" : `linear-gradient(135deg, ${Colors.bgTertiary}, ${Colors.bgElevated})`,
              border: `2px dashed ${Colors.borderPrimary}`, cursor: "pointer",
            }}
            onClick={() => fileRef.current?.click()}
          >
            {imagePreview
              ? <img src={imagePreview} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
              : <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "100%", gap: 1 }}>
                  <MdImage size={36} color={Colors.textSecondary} />
                  <Typography variant="caption" sx={{ color: Colors.textSecondary }}>Click para subir imagen</Typography>
                </Box>
            }
            {imagePreview && (
              <Box sx={{ position: "absolute", inset: 0, backgroundColor: "rgba(0,0,0,0.4)", display: "flex", alignItems: "center", justifyContent: "center", opacity: 0, transition: "opacity 0.2s", "&:hover": { opacity: 1 } }}>
                <Typography variant="caption" sx={{ color: "#fff", fontWeight: 600 }}>Cambiar imagen</Typography>
              </Box>
            )}
          </Box>
          <input ref={fileRef} type="file" accept="image/*" style={{ display: "none" }} onChange={handleImagePick} />
          {saving && uploadPct > 0 && uploadPct < 100 && <LinearProgress variant="determinate" value={uploadPct} sx={{ borderRadius: 4 }} />}

          <TextField label="Nombre *" value={name} onChange={(e) => setName(e.target.value)} fullWidth />
          <TextField label="Descripción" value={description} onChange={(e) => setDescription(e.target.value)} fullWidth multiline minRows={2} />
          <TextField label="Stream URL (Zoom / Live)" value={streamURL} onChange={(e) => setStreamURL(e.target.value)} fullWidth placeholder="https://zoom.us/..." />
          <TextField label="Video URL (grabado)" value={videoURL} onChange={(e) => setVideoURL(e.target.value)} fullWidth placeholder="https://vimeo.com/..." />
          <TextField label="Link / Acción" value={clickMe} onChange={(e) => setClickMe(e.target.value)} fullWidth placeholder="https://..." />
          <TextField label="Orden" type="number" value={orderNumber} onChange={(e) => setOrderNumber(e.target.value)} fullWidth />

          <Divider sx={{ borderColor: Colors.borderSecondary }} />

          <Stack spacing={0.5}>
            <FormControlLabel control={<Switch checked={isActive}      onChange={(e) => setIsActive(e.target.checked)}      color="primary" />} label={<Typography sx={{ color: Colors.textPrimary, fontSize: "0.9rem" }}>Activo</Typography>} />
            <TextField select label="Plan mínimo" value={accessKey} onChange={(e) => setAccessKey(e.target.value)} fullWidth SelectProps={{ native: true }}>
              <option value="public">Público</option>
              <option value="access">NuCore Access</option>
              <option value="pro">NuCore Pro</option>
              <option value="elite">NuCore Elite</option>
            </TextField>
          </Stack>

          <Divider sx={{ borderColor: Colors.borderSecondary }} />

          <Stack direction="row" spacing={1.5} justifyContent="flex-end">
            <Button variant="outlined" onClick={onClose} disabled={saving}
              sx={{ textTransform: "none", borderRadius: 2, borderColor: Colors.borderPrimary, color: Colors.textSecondary }}>
              Cancelar
            </Button>
            <Button variant="contained" onClick={handleSave} disabled={saving}
              sx={{ textTransform: "none", borderRadius: 2, backgroundColor: Colors.primary, "&:hover": { backgroundColor: Colors.primaryDark } }}>
              {saving ? "Guardando..." : "Guardar"}
            </Button>
          </Stack>
        </Stack>
      </Box>
    </Drawer>
  );
}

// ── HomeConfigView ────────────────────────────────────────────────────────────

export default function HomeConfigView() {
  const [tab, setTab] = useState(0);

  return (
    <Box sx={{ width: "100%", py: 3 }}>
      <Container maxWidth="xl">

        {/* Header */}
        <Paper sx={{ mb: 3, p: 2.5, borderRadius: 3, background: `linear-gradient(135deg, ${Colors.bgSecondary} 0%, ${Colors.bgTertiary} 100%)`, border: `1px solid ${Colors.borderSecondary}` }}>
          <Typography variant="h5" sx={{ color: Colors.textPrimary }}>Banners de Inicio</Typography>
          <Typography variant="body2" sx={{ color: Colors.textSecondary, mt: 0.5 }}>
            Banners y tarjetas que aparecen en la pantalla principal de la app móvil.
          </Typography>

          <Tabs
            value={tab}
            onChange={(_, v) => setTab(v)}
            sx={{
              mt: 2,
              "& .MuiTab-root": { color: Colors.textSecondary, textTransform: "none", fontSize: "0.875rem" },
              "& .Mui-selected": { color: Colors.textPrimary },
              "& .MuiTabs-indicator": { backgroundColor: Colors.primary },
            }}
          >
            <Tab label="Owners Banner" />
            {ITEM_TABS.map((t) => <Tab key={t.key} label={t.label} />)}
          </Tabs>
        </Paper>

        {/* Tab panels */}
        {tab === 0 && <OwnersBannerPanel />}
        {ITEM_TABS.map((t, i) =>
          tab === i + 1
            ? <HomeBannersTab key={t.key} tab={t} />
            : null
        )}

      </Container>
    </Box>
  );
}
